import { PortableText } from "@portabletext/react";
import { useEffect, useState } from "react";
import { PortableTextBlock } from "sanity";

import {
  components,
  componentsNew,
  getHeadingId,
  toPlainText,
} from "@/components/portableTextComponents";
import { Button, ButtonAnchor } from "@/components/ui/button";
import {
  DropdownMenu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@/components/ui/menu";

export interface Heading {
  text: PortableTextBlock;
  level: number;
  id: string;
}

export function DocsPortableText({
  body,
  isNew,
}: {
  body: unknown;
  isNew?: boolean;
}) {
  return (
    <div suppressHydrationWarning className="break-words">
      <PortableText
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
        value={body as any}
        components={isNew ? componentsNew : components}
      />
    </div>
  );
}

export function DocsTableOfContents({ headings }: { headings: Heading[] }) {
  const selectedHeading = useActiveHeading();

  return (
    <ul className="flex flex-col gap-2">
      {headings.map((heading) => {
        const id = getHeadingId(heading.text);
        const indent = heading.level - 2;
        return (
          <li key={id}>
            <ButtonAnchor
              variant={selectedHeading === id ? "action" : "ghost"}
              className="justify-start w-full text-wrap"
              size="xs"
              style={{ paddingLeft: `${12 + indent * 20}px` }}
              href={`#${id}`}
            >
              {toPlainText([heading.text])}
            </ButtonAnchor>
          </li>
        );
      })}
    </ul>
  );
}

function useActiveHeading() {
  const [selectedHeading, setSelectedHeading] = useState<string | null>(null);

  useEffect(() => {
    const headings = document.querySelectorAll("h1, h2, h3, h4, h5, h6");
    const observer = new IntersectionObserver((entries) => {
      entries.find((entry) => {
        if (entry.isIntersecting) {
          setSelectedHeading(entry.target.id);
          return true;
        }
      });
    });

    headings.forEach((heading) => {
      observer.observe(heading);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return selectedHeading;
}

export function MobileTableOfContents({ headings }: { headings: Heading[] }) {
  const selectedHeading = useActiveHeading();
  const setSelectedHeading = (value: string) => {
    document.getElementById(value)?.scrollIntoView({ behavior: "smooth" });
  };
  const text = selectedHeading
    ? // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      headings.find((h) => h.id === selectedHeading)?.text!
    : headings[0]?.text;

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <MenuTrigger asChild>
          <Button variant="outline" size="xs">
            Jump to
          </Button>
        </MenuTrigger>
        <MenuContent
          side="right"
          sideOffset={16}
          align="start"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {headings.map((heading) => {
            const id = getHeadingId(heading.text);
            return (
              <MenuItem key={id} onSelect={() => setSelectedHeading(id)}>
                {toPlainText([heading.text])}
              </MenuItem>
            );
          })}
        </MenuContent>
      </DropdownMenu>
      {text && (
        <div className="text-sm text-slate-dim">{toPlainText([text])}</div>
      )}
    </div>
  );
}
